import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import API, { ErrorResponse, PaginatedResponse } from '../../api/Api';
import { useAppDispatch, useAppSelector } from '../../shared/redux/store';
import { useAppContext } from '../App.Context';
import Profile from '../../models/Profile';
import Student, { studentSearchParams } from '../../models/Student';
import User from '../../models/User';

export const fetchCurrentUserProfile = createAsyncThunk(
  'user/:id',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getUserProfile();
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Student>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchCurrentStudent = createAsyncThunk<PaginatedResponse<Student>, studentSearchParams>(
  'student',
  async (parameters, thunkAPI) => {
    try {
      const response = await API.getStudent(parameters);
      return response;
    } catch (err) {
      const error = err as ErrorResponse<Student>
      if (!error.response) {
        throw err;
      }
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

interface UserStoreInterface {
  profile?: Profile,
  isLoadingProfile: boolean
  rejectedUserProfile: boolean
  student?: Student
  user?: User & Profile
}

const initialState: UserStoreInterface = {
  profile: undefined,
  isLoadingProfile: false,
  rejectedUserProfile: false,
  student: undefined,
  user: undefined
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    reset: (state = initialState) => {
      state.profile = undefined;
      state.isLoadingProfile = false;
      state.rejectedUserProfile = false;
      state.student = undefined;
      state.user = undefined;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCurrentUserProfile.pending, (state) => {
      state.isLoadingProfile = true;
      state.rejectedUserProfile = false;
    });

    builder.addCase(fetchCurrentUserProfile.fulfilled, (state, action) => {
      state.isLoadingProfile = false;
      state.profile = action.payload;
    });

    builder.addCase(fetchCurrentUserProfile.rejected, (state) => {
      state.isLoadingProfile = false;
      state.rejectedUserProfile = true;
    });

    builder.addCase(fetchCurrentStudent.fulfilled, (state, action) => {
      if (action.payload.items.length > 0) {
        state.student = action.payload.items[0];
      }
    });
  }
});

export const { reset } = userSlice.actions;

export default userSlice.reducer;

export const useCurrentUserProfile = (): Profile | undefined => {
  const { user } = useAppContext();
  const dispatch = useAppDispatch();
  const currentUserProfile = useAppSelector((state) => state.user?.profile);

  useEffect(() => {
    user.valid && dispatch(fetchCurrentUserProfile());
  }, [user]);

  return currentUserProfile;
};

export const useCurrentStudent = (): Student | undefined => {
  const profile = useCurrentUserProfile();
  const dispatch = useAppDispatch();
  const currentUserStudent = useAppSelector((state) => state.user.student);

  useEffect(() => {
    profile && dispatch(fetchCurrentStudent({
      pattern: `${profile.name} ${profile.lastName}`,
      document_type: profile.documentType.name,
      document_number: profile.documentNumber,
      offset: 0,
      pageSize: 1
    }));
  }, [profile]);

  return currentUserStudent;
};
